import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TurndownService from 'turndown';
import html2canvas from 'html2canvas';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Mail, Bell, Users, FileText, RefreshCw, Zap, Swords, Upload, ExternalLink, Image, Sparkles, Wand2, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [picks, setPicks] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPick, setEditingPick] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('picks');
  const [subscribers, setSubscribers] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportNewCount, setSupportNewCount] = useState(0);
  const [referralStats, setReferralStats] = useState(null);
  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '' });
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleFormData, setArticleFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    published: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [findingRelated, setFindingRelated] = useState(false);
  const [suggestedSources, setSuggestedSources] = useState([]);
  const [autoUpdating, setAutoUpdating] = useState(false);
  const [cleaningContent, setCleaningContent] = useState(false);
  const [cleanupChanges, setCleanupChanges] = useState([]);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [resultsSummary, setResultsSummary] = useState(null);
  const [regenProgress, setRegenProgress] = useState(null); // { current, total, status }
  const thumbRenderRef = useRef(null);
  const [challengeEntries, setChallengeEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [formData, setFormData] = useState({
    sport: 'NBA',
    player_name: '',
    prop_type: '',
    line: '',
    pick: '',
    reasoning: '',
    player_stats: '',
    matchup_analysis: '',
    confidence_level: 7,
    youtube_url: '',
    thumbnail_url: '',
    featured: false,
    published: true,
    affiliate_links: [],
    // Intelligence Report Fields
    game: '',
    opponent: '',
    // Outlier Projection Data
    last_5_avg: '',
    last_10_avg: '',
    last_20_avg: '',
    season_avg: '',
    season_median: '',
    l5_gap: '',
    l10_gap: '',
    l20_gap: '',
    season_median_gap: '',
    median_vs_line_gap: '',
    structural_cushion: '',
    // Structural Edge
    structural_edge_class: '',
    structural_edge_analysis: '',
    // Trend Data
    last_5_hit_rate: '',
    last_5_median: '',
    last_10_hit_rate: '',
    last_10_median: '',
    last_20_hit_rate: '',
    last_20_median: '',
    season_hit_rate: '',
    // Head-to-Head
    h2h_hit_rate: '',
    h2h_outcomes: '',
    h2h_avg: '',
    h2h_median: '',
    h2h_analysis: '',
    // Matchup Context
    opp_pts_allowed: '',
    opp_pts_rank: '',
    opp_reb_allowed: '',
    opp_reb_rank: '',
    opp_ast_allowed: '',
    opp_ast_rank: '',
    matchup_interpretation: '',
    // Pace
    opp_pace_rank: '',
    pace_analysis: '',
    // Defense vs Position
    dvp_pts: '',
    dvp_reb: '',
    dvp_ast: '',
    dvp_overall: '',
    // Supporting Metrics
    potential_assists: '',
    rebound_chances: '',
    projected_minutes: '',
    fg_attempts: '',
    // Public Betting
    public_bet_pct: '',
    public_money_pct: '',
    betting_interpretation: '',
    // Edge Summary
    edge_signals: '',
    investigative_analysis: '',
    // Decision Card
    grade: '',
    action: '',
    action_reason: '',
    distribution_interpretation: '',
    opportunity_signals: '',
    ceiling_potential: '',
    risk_conditions: '',
    kill_switch: '',
    // PSL Score
    psl_prop_score: '',
    score_explanation: '',
    // Heat Meter
    heat_level: '',
    heat_score_range: '',
    // Distribution Outlook
    expected_range: '',
    median_projection: '',
    clearance_probability_estimate: '',
    // Executive Summary
    slate_type: '',
    slate_size: '',
    overall_confidence: '',
    risk_meter: '',
    approval_status: ''
  });
  const [affiliateInput, setAffiliateInput] = useState({ text: '', url: '' });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPicks();
      fetchSubscribers();
      fetchArticles();
      fetchResultsSummary();
      fetchChallengeEntries();
      fetchSupportData();
    }
  }, [isAuthenticated]);

  // Deep-link: auto-open article editor when ?edit_article=ID is in the URL
  useEffect(() => {
    const editArticleId = searchParams.get('edit_article');
    if (editArticleId && articles.length > 0) {
      const article = articles.find(a => a.id === editArticleId);
      if (article) {
        setActiveTab('articles');
        handleEditArticle(article);
        // Clear the param so refresh doesn't re-trigger
        searchParams.delete('edit_article');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [articles, searchParams]);

  const fetchChallengeEntries = async () => {
    setLoadingEntries(true);
    try {
      const response = await axios.get(`${API}/challenge/entries`);
      setChallengeEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch challenge entries:', error);
    } finally {
      setLoadingEntries(false);
    }
  };

  const fetchResultsSummary = async () => {
    try {
      const response = await axios.get(`${API}/results/summary`);
      setResultsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch results summary:', error);
    }
  };

  const handleAutoUpdateResults = async () => {
    setAutoUpdating(true);
    try {
      const response = await axios.post(`${API}/picks/auto-update-results`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      if (response.data.updated > 0) {
        fetchPicks();
        fetchResultsSummary();
      }
    } catch (error) {
      console.error('Auto-update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to auto-update results');
    } finally {
      setAutoUpdating(false);
    }
  };

  const fetchPicks = async () => {
    try {
      const response = await axios.get(`${API}/admin/picks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPicks(response.data);
    } catch (error) {
      console.error('Failed to fetch picks:', error);
      toast.error('Failed to load picks');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    }
  };

  const fetchSupportData = async () => {
    try {
      const [ticketsRes, referralsRes] = await Promise.allSettled([
        axios.get(`${API}/admin/support-tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/referrals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (ticketsRes.status === 'fulfilled') {
        setSupportTickets(ticketsRes.value.data.tickets);
        setSupportNewCount(ticketsRes.value.data.new_count);
      }
      if (referralsRes.status === 'fulfilled') setReferralStats(referralsRes.value.data);
    } catch {}
  };

  const handleToggleTicketStatus = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/support-tickets/${ticketId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSupportData();
      toast.success('Ticket updated');
    } catch { toast.error('Failed to update ticket'); }
  };

  const handleClearCache = async () => {
    try {
      const res = await axios.post(`${API}/admin/clear-cache`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch { toast.error('Cache clear failed'); }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/subscribers`, newSubscriber, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscriber added!');
      setSubscriberDialogOpen(false);
      setNewSubscriber({ email: '', name: '' });
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscriber');
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${subscriberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleSendNotification = async (pickId) => {
    if (!window.confirm('Send email notification to all subscribers for this pick?')) return;
    try {
      const response = await axios.post(`${API}/admin/send-notification/${pickId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    }
  };

  // Article management functions
  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...articleFormData,
        featured_image: articleFormData.featured_image || null
      };
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article updated successfully!');
      } else {
        await axios.post(`${API}/articles`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article created successfully!');
      }
      setArticleDialogOpen(false);
      setEditingArticle(null);
      setArticleFormData({ title: '', excerpt: '', content: '', featured_image: '', published: true });
      setSuggestedSources([]);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    }
  };

  const handleArticleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      // Store only the relative path (e.g., "/api/images/abc123") so it works across environments
      const imageUrl = response.data.url;
      setArticleFormData(prev => ({ ...prev, featured_image: imageUrl }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingThumbnail(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      // Store only the relative path (e.g., "/api/images/abc123") so it works across environments
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, thumbnail_url: imageUrl }));
      toast.success('Thumbnail uploaded!');
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const findRelatedArticles = async () => {
    if (!articleFormData.title || !articleFormData.content) {
      toast.error('Please add a title and content first');
      return;
    }
    
    setFindingRelated(true);
    setSuggestedSources([]);
    try {
      const response = await axios.post(
        `${API}/articles/find-related`,
        { title: articleFormData.title, content: articleFormData.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestedSources(response.data.suggested_sources || []);
      toast.success('Found related articles!');
    } catch (error) {
      console.error('Error finding related articles:', error);
      toast.error('Failed to find related articles');
    } finally {
      setFindingRelated(false);
    }
  };

  const handleAutoGenerate = async (contentType) => {
    setGeneratingContent(true);
    try {
      const res = await axios.post(`${API}/articles/auto-generate`, 
        { content_type: contentType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  // Bulk regenerate all thumbnails with current record
  const handleBulkRegenThumbnails = async () => {
    if (!picks.length) {
      toast.error('No picks to regenerate');
      return;
    }
    
    // Fetch current record
    let record = { wins: 0, losses: 0 };
    try {
      const res = await axios.get(`${API}/results/summary`);
      record = { wins: res.data.wins, losses: res.data.losses };
    } catch (e) {
      toast.error('Failed to fetch record');
      return;
    }
    
    const winRate = (record.wins + record.losses) > 0 
      ? ((record.wins / (record.wins + record.losses)) * 100).toFixed(1) 
      : '0';
    
    setRegenProgress({ current: 0, total: picks.length, status: 'Starting...' });
    
    let successCount = 0;
    let skipCount = 0;
    
    for (let i = 0; i < picks.length; i++) {
      const pick = picks[i];
      setRegenProgress({ current: i + 1, total: picks.length, status: `${pick.player_name}...` });
      
      // Yield to browser every pick to prevent "page unresponsive"
      await new Promise(r => setTimeout(r, 50));
      
      try {
        const container = thumbRenderRef.current;
        if (!container) continue;
        
        const stat = `${pick.pick} ${pick.line} ${pick.prop_type.replace(/Points/gi,'PTS').replace(/Rebounds/gi,'REB').replace(/Assists/gi,'AST').replace(/Steals/gi,'STL').replace(/Blocks/gi,'BLK')}`;
        
        container.innerHTML = `
          <div data-thumb-canvas="true" style="width:1280px;height:720px;position:relative;overflow:hidden;background:linear-gradient(135deg,#000,#111,#000);font-family:system-ui,-apple-system,sans-serif;">
            <div style="position:absolute;top:20px;left:20px;z-index:30;">
              <span style="background:#4ade80;color:#000;padding:8px 16px;font-size:18px;font-weight:900;text-transform:uppercase;letter-spacing:2px;">FREE PICKS</span>
            </div>
            <div style="position:absolute;right:0;top:0;height:100%;width:55%;display:flex;flex-direction:column;justify-content:center;padding-right:48px;z-index:20;">
              <div style="margin-bottom:16px;">
                <span style="background:#4ade80;color:#000;padding:8px 16px;font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:3px;">NBA</span>
              </div>
              <h1 style="font-size:56px;font-weight:900;color:#fff;line-height:1.1;margin:0 0 8px 0;text-transform:uppercase;">${pick.player_name}</h1>
              <div style="font-size:32px;font-weight:700;color:#4ade80;margin-bottom:24px;">${stat}</div>
              <div style="display:flex;gap:32px;">
                <div style="background:rgba(0,0,0,0.5);border:2px solid rgba(74,222,128,0.3);padding:12px 24px;">
                  <div style="font-size:28px;font-weight:900;color:#4ade80;">${winRate}%</div>
                  <div style="color:#9ca3af;font-size:11px;text-transform:uppercase;letter-spacing:2px;">Win Rate</div>
                </div>
                <div style="background:rgba(0,0,0,0.5);border:2px solid rgba(74,222,128,0.3);padding:12px 24px;">
                  <div style="font-size:28px;font-weight:900;color:#fff;">${record.wins}-${record.losses}</div>
                  <div style="color:#9ca3af;font-size:11px;text-transform:uppercase;letter-spacing:2px;">Record</div>
                </div>
              </div>
              <div style="margin-top:32px;display:flex;align-items:center;gap:8px;">
                <div style="width:32px;height:32px;background:#4ade80;border-radius:4px;display:flex;align-items:center;justify-content:center;">
                  <span style="color:#000;font-weight:700;font-size:12px;">PSL</span>
                </div>
                <span style="color:#9ca3af;font-size:14px;letter-spacing:1px;">PRIMESTATLAB.COM</span>
              </div>
            </div>
          </div>
        `;
        
        // Small delay for DOM render
        await new Promise(r => setTimeout(r, 100));
        
        // Capture with timeout — skip if takes too long
        const targetEl = container.querySelector('[data-thumb-canvas]') || container.firstElementChild;
        const canvasPromise = html2canvas(targetEl, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#000000',
          logging: false,
          width: 1280,
          height: 720
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 8000)
        );
        
        const canvas = await Promise.race([canvasPromise, timeoutPromise]);
        
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        
        const uploadData = new FormData();
        uploadData.append('file', blob, `thumb-${pick.player_name.replace(/\s+/g, '-')}.jpg`);
        
        const uploadRes = await axios.post(`${API}/upload-image`, uploadData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        
        await axios.patch(`${API}/picks/${pick.id}/thumbnail`, 
          { thumbnail_url: uploadRes.data.url },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        successCount++;
      } catch (err) {
        skipCount++;
        console.error(`Skipped ${pick.player_name}:`, err.message || err);
      }
    }
    
    // Clear hidden container
    if (thumbRenderRef.current) thumbRenderRef.current.innerHTML = '';
    setRegenProgress(null);
    const msg = skipCount > 0 
      ? `Done! ${successCount} regenerated, ${skipCount} skipped.`
      : `Regenerated all ${successCount} thumbnails with updated record!`;
    toast.success(msg);
    fetchPicks();
  };

  const handleAICleanup = async (cleanupType = 'format') => {
    if (!articleFormData.content) {
      toast.error('Please add content first');
      return;
    }
    
    setCleaningContent(true);
    setCleanupChanges([]);
    try {
      const response = await axios.post(
        `${API}/articles/ai-cleanup`,
        { content: articleFormData.content, cleanup_type: cleanupType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArticleFormData(prev => ({ ...prev, content: response.data.cleaned_content }));
      setCleanupChanges(response.data.changes_made || []);
      toast.success('Content cleaned up successfully!');
    } catch (error) {
      console.error('Error cleaning content:', error);
      toast.error('Failed to clean content: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCleaningContent(false);
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setArticleFormData({
      title: article.title,
      excerpt: article.excerpt || '',
      content: article.content,
      featured_image: article.featured_image || '',
      published: article.published
    });
    setSuggestedSources([]);
    setArticleDialogOpen(true);
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert string values to proper types for numeric fields
      const numericFields = [
        'confidence_level', 'last_5_avg', 'last_10_avg', 'last_20_avg', 'season_avg', 'season_median',
        'l5_gap', 'l10_gap', 'l20_gap', 'season_median_gap', 'median_vs_line_gap',
        'last_5_median', 'last_10_median', 'last_20_median',
        'h2h_avg', 'h2h_median', 'opp_pts_allowed', 'opp_pts_rank', 'opp_reb_allowed', 'opp_reb_rank',
        'opp_ast_allowed', 'opp_ast_rank', 'potential_assists', 'rebound_chances', 'projected_minutes',
        'psl_prop_score'
      ];
      
      const cleanedData = { ...formData };
      numericFields.forEach(field => {
        if (cleanedData[field] !== '' && cleanedData[field] !== null && cleanedData[field] !== undefined) {
          const parsed = parseFloat(cleanedData[field]);
          if (!isNaN(parsed)) {
            cleanedData[field] = parsed;
          } else {
            // If it can't be parsed, set to null so backend ignores it
            cleanedData[field] = null;
          }
        } else {
          cleanedData[field] = null;
        }
      });

      if (editingPick) {
        await axios.put(`${API}/picks/${editingPick.id}`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pick updated successfully!');
      } else {
        await axios.post(`${API}/picks`, cleanedData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pick created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchPicks();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects)
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Format validation errors nicely
        const errorMessages = errorDetail.map(err => `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`).join('; ');
        toast.error(errorMessages || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to save pick');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pickId) => {
    if (!window.confirm('Are you sure you want to delete this pick?')) return;

    try {
      await axios.delete(`${API}/picks/${pickId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pick deleted successfully!');
      fetchPicks();
    } catch (error) {
      toast.error('Failed to delete pick');
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const csv = event.target.result;
        
        // Parse CSV properly handling quoted values
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const lines = csv.split('\n');
        const headers = parseCSVLine(lines[0]);
        
        // Map header names to field names
        const headerMap = {
          'sport': 'sport',
          'player_name': 'player_name',
          'player name': 'player_name',
          'player': 'player_name',
          'prop type': 'prop_type',
          'prop_type': 'prop_type',
          'line': 'line',
          'pick': 'pick',
          'game': 'game',
          'opponent': 'opponent',
          'reasoning': 'reasoning',
          'player stats': 'player_stats',
          'matchup analysis': 'matchup_analysis',
          'matchup_analysis': 'matchup_analysis',
          'confidence (1-10)': 'confidence_level',
          'confidence_level': 'confidence_level',
          'last 5 avg': 'last_5_avg',
          'last_5_avg': 'last_5_avg',
          'l5_avg': 'last_5_avg',
          'l5 avg': 'last_5_avg',
          'last 10 avg': 'last_10_avg',
          'last_10_avg': 'last_10_avg',
          'l10_avg': 'last_10_avg',
          'l10 avg': 'last_10_avg',
          'last 20 avg': 'last_20_avg',
          'last_20_avg': 'last_20_avg',
          'l20_avg': 'last_20_avg',
          'l20 avg': 'last_20_avg',
          'season avg': 'season_avg',
          'season_avg': 'season_avg',
          'season median': 'season_median',
          'season_median': 'season_median',
          'l5 gap': 'l5_gap',
          'l5_gap': 'l5_gap',
          'gap_last5': 'l5_gap',
          'gap last5': 'l5_gap',
          'l10 gap': 'l10_gap',
          'l10_gap': 'l10_gap',
          'gap_last10': 'l10_gap',
          'gap last10': 'l10_gap',
          'l20 gap': 'l20_gap',
          'l20_gap': 'l20_gap',
          'gap_last20': 'l20_gap',
          'gap last20': 'l20_gap',
          'season median gap': 'season_median_gap',
          'season_median_gap': 'season_median_gap',
          'gap_season': 'season_median_gap',
          'gap season': 'season_median_gap',
          'median_vs_line_gap': 'median_vs_line_gap',
          'median vs line gap': 'median_vs_line_gap',
          'structural cushion': 'structural_cushion',
          'structural_cushion': 'structural_cushion',
          'structural edge class': 'structural_edge_class',
          'structural_edge_class': 'structural_edge_class',
          'structural edge analysis': 'structural_edge_analysis',
          'structural_edge_analysis': 'structural_edge_analysis',
          'last 5 hit rate': 'last_5_hit_rate',
          'last_5_hit_rate': 'last_5_hit_rate',
          'l5 hit rate': 'last_5_hit_rate',
          'last 5 median': 'last_5_median',
          'last_5_median': 'last_5_median',
          'last 10 hit rate': 'last_10_hit_rate',
          'last_10_hit_rate': 'last_10_hit_rate',
          'l10 hit rate': 'last_10_hit_rate',
          'last 10 median': 'last_10_median',
          'last_10_median': 'last_10_median',
          'last 20 hit rate': 'last_20_hit_rate',
          'last_20_hit_rate': 'last_20_hit_rate',
          'l20 hit rate': 'last_20_hit_rate',
          'last 20 median': 'last_20_median',
          'last_20_median': 'last_20_median',
          'season hit rate': 'season_hit_rate',
          'season_hit_rate': 'season_hit_rate',
          'h2h hit rate': 'h2h_hit_rate',
          'h2h_hit_rate': 'h2h_hit_rate',
          'h2h outcomes': 'h2h_outcomes',
          'h2h_outcomes': 'h2h_outcomes',
          'h2h avg': 'h2h_avg',
          'h2h_avg': 'h2h_avg',
          'h2h median': 'h2h_median',
          'h2h_median': 'h2h_median',
          'h2h analysis': 'h2h_analysis',
          'h2h_analysis': 'h2h_analysis',
          'opp pts allowed': 'opp_pts_allowed',
          'opp pts rank': 'opp_pts_rank',
          'opp reb allowed': 'opp_reb_allowed',
          'opp reb rank': 'opp_reb_rank',
          'opp ast allowed': 'opp_ast_allowed',
          'opp ast rank': 'opp_ast_rank',
          'matchup interpretation': 'matchup_interpretation',
          'matchup_interpretation': 'matchup_interpretation',
          'opp pace rank': 'opp_pace_rank',
          'opp_pace_rank': 'opp_pace_rank',
          'pace analysis': 'pace_analysis',
          'pace_analysis': 'pace_analysis',
          'dvp pts': 'dvp_pts',
          'dvp reb': 'dvp_reb',
          'dvp ast': 'dvp_ast',
          'dvp overall': 'dvp_overall',
          'potential assists': 'potential_assists',
          'rebound chances': 'rebound_chances',
          'projected minutes': 'projected_minutes',
          'fg attempts': 'fg_attempts',
          'public bet pct': 'public_bet_pct',
          'public bet %': 'public_bet_pct',
          'public money pct': 'public_money_pct',
          'public money %': 'public_money_pct',
          'betting interpretation': 'betting_interpretation',
          'edge signals': 'edge_signals',
          'edge_signals': 'edge_signals',
          'investigative analysis': 'investigative_analysis',
          'investigative_analysis': 'investigative_analysis',
          'grade': 'grade',
          'operator grade': 'grade',
          'action': 'action',
          'operator action': 'action',
          'action reason': 'action_reason',
          'action_reason': 'action_reason',
          'distribution interpretation': 'distribution_interpretation',
          'distribution_interpretation': 'distribution_interpretation',
          'opportunity signals': 'opportunity_signals',
          'opportunity_signals': 'opportunity_signals',
          'ceiling potential': 'ceiling_potential',
          'ceiling_potential': 'ceiling_potential',
          'risk conditions': 'risk_conditions',
          'risk_conditions': 'risk_conditions',
          'kill switch': 'kill_switch',
          'kill_switch': 'kill_switch',
          'psl prop score': 'psl_prop_score',
          'psl_prop_score': 'psl_prop_score',
          'score explanation': 'score_explanation',
          'score_explanation': 'score_explanation',
          'heat level': 'heat_level',
          'heat_level': 'heat_level',
          'heat score range': 'heat_score_range',
          'heat_score_range': 'heat_score_range',
          'expected_range': 'expected_range',
          'expected range': 'expected_range',
          'median_projection': 'median_projection',
          'median projection': 'median_projection',
          'clearance_probability_estimate': 'clearance_probability_estimate',
          'clearance probability estimate': 'clearance_probability_estimate',
          'clearance_probability': 'clearance_probability_estimate',
          'clearance probability': 'clearance_probability_estimate',
          'slate type': 'slate_type',
          'slate_type': 'slate_type',
          'slate size': 'slate_size',
          'slate_size': 'slate_size',
          'overall confidence': 'overall_confidence',
          'overall_confidence': 'overall_confidence',
          'risk meter': 'risk_meter',
          'risk_meter': 'risk_meter',
          'approval status': 'approval_status',
          'approval_status': 'approval_status',
          'featured (true/false)': 'featured',
          'published (true/false)': 'published',
          'youtube url': 'youtube_url',
          'export date': 'export_date',
          // Alternative column names from user exports
          'edge_grade': 'grade',
          'edge grade': 'grade',
          'psl_score': 'psl_prop_score',
          'psl score': 'psl_prop_score',
          'heat_classification': 'heat_level',
          'heat classification': 'heat_level',
          // Additional mappings from Download Factory CSV format
          'thumbnail_image': 'thumbnail_url',
          'thumbnail image': 'thumbnail_url',
          'featured_pick': 'featured',
          'featured pick': 'featured',
          'season_gap': 'season_median_gap',
          'season gap': 'season_median_gap',
          'edge_classification': 'structural_edge_class',
          'edge classification': 'structural_edge_class',
          'edge_analysis': 'structural_edge_analysis',
          'edge analysis': 'structural_edge_analysis',
          'l5_hit_rate': 'last_5_hit_rate',
          'l10_hit_rate': 'last_10_hit_rate',
          'l20_hit_rate': 'last_20_hit_rate',
          'l5_hit_rate_over': 'last_5_hit_rate',
          'l10_hit_rate_over': 'last_10_hit_rate',
          'l20_hit_rate_over': 'last_20_hit_rate',
          'reb_rank': 'opp_reb_rank',
          'ast_rank': 'opp_ast_rank',
          'key_edge_signals': 'edge_signals',
          'key edge signals': 'edge_signals',
          'grade_display': 'grade',
          'action_display': 'action',
          'opp_pts_allowed': 'opp_pts_allowed',
          'opp_pts_rank': 'opp_pts_rank',
          'opp_reb_allowed': 'opp_reb_allowed',
          'opp_ast_allowed': 'opp_ast_allowed',
          'potential_assists': 'potential_assists',
          'rebound_chances': 'rebound_chances',
          'projected_minutes': 'projected_minutes',
          'fg_attempts': 'fg_attempts',
          'public_bet_pct': 'public_bet_pct',
          'public_money_pct': 'public_money_pct',
          'betting_interpretation': 'betting_interpretation',
          'player_stats': 'player_stats',
          'youtube_url': 'youtube_url',
          'affiliate_link_text_1': 'affiliate_link_text_1',
          'affiliate_link_url_1': 'affiliate_link_url_1',
          'affiliate_link_text_2': 'affiliate_link_text_2',
          'affiliate_link_url_2': 'affiliate_link_url_2',
          'dvp_pts': 'dvp_pts',
          'dvp_reb': 'dvp_reb',
          'dvp_ast': 'dvp_ast',
          'dvp_overall': 'dvp_overall',
          'published': 'published'
        };
        
        // Number fields that need parsing
        const numberFields = [
          'confidence_level', 'last_5_avg', 'last_10_avg', 'last_20_avg', 'season_avg', 'season_median',
          'l5_gap', 'l10_gap', 'l20_gap', 'season_median_gap', 'median_vs_line_gap', 'last_5_median', 'last_10_median', 'last_20_median',
          'h2h_avg', 'h2h_median', 'opp_pts_allowed', 'opp_pts_rank', 'opp_reb_allowed', 'opp_reb_rank',
          'opp_ast_allowed', 'opp_ast_rank', 'potential_assists', 'rebound_chances', 'projected_minutes', 'psl_prop_score'
        ];
        
        const picks = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = parseCSVLine(lines[i]);
          const pick = {
            sport: 'NBA',
            affiliate_links: []
          };
          
          // Map each header to its field
          headers.forEach((header, idx) => {
            const fieldName = headerMap[header.toLowerCase()];
            if (fieldName && values[idx] !== undefined && values[idx] !== '') {
              let value = values[idx];
              
              // Parse boolean fields
              if (fieldName === 'featured' || fieldName === 'published') {
                value = value.toLowerCase() === 'true';
              }
              // Parse number fields
              else if (numberFields.includes(fieldName)) {
                const parsed = parseFloat(value.replace(/[+%]/g, ''));
                value = isNaN(parsed) ? null : parsed;
              }
              
              pick[fieldName] = value;
            }
          });
          
          // Set defaults for required fields
          if (!pick.confidence_level) pick.confidence_level = 7;
          if (!pick.reasoning) pick.reasoning = pick.action_reason || pick.structural_edge_analysis || 'Analysis pending';
          if (pick.featured === undefined) pick.featured = false;
          if (pick.published === undefined) pick.published = true;
          
          // Handle affiliate links from separate columns
          if (pick.affiliate_link_text_1 && pick.affiliate_link_url_1) {
            pick.affiliate_links.push({
              text: pick.affiliate_link_text_1,
              url: pick.affiliate_link_url_1
            });
            delete pick.affiliate_link_text_1;
            delete pick.affiliate_link_url_1;
          }
          if (pick.affiliate_link_text_2 && pick.affiliate_link_url_2) {
            pick.affiliate_links.push({
              text: pick.affiliate_link_text_2,
              url: pick.affiliate_link_url_2
            });
            delete pick.affiliate_link_text_2;
            delete pick.affiliate_link_url_2;
          }
          
          // Auto-generate sport if missing (default to NBA)
          if (!pick.sport) pick.sport = 'NBA';
          
          // Auto-generate pick direction if missing based on action field
          if (!pick.pick && pick.line && pick.prop_type) {
            const action = (pick.action || '').toLowerCase();
            if (action.includes('over') || action.includes('play over')) {
              pick.pick = `Over ${pick.line} ${pick.prop_type}`;
            } else if (action.includes('under') || action.includes('play under')) {
              pick.pick = `Under ${pick.line} ${pick.prop_type}`;
            } else if (action.includes('play')) {
              // Default to Over if just "PLAY" without direction
              pick.pick = `Over ${pick.line} ${pick.prop_type}`;
            } else if (action.includes('pass') || action.includes('fade')) {
              pick.pick = 'Pass';
            } else {
              // Default to Over
              pick.pick = `Over ${pick.line} ${pick.prop_type}`;
            }
          }
          
          if (pick.player_name && pick.prop_type && pick.line) {
            picks.push(pick);
          }
        }

        if (picks.length === 0) {
          toast.error('No valid picks found in CSV');
          setUploading(false);
          return;
        }

        const response = await axios.post(`${API}/picks/bulk-upload`, picks, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`Successfully uploaded ${response.data.count} picks!`);
        setUploadDialogOpen(false);
        setCsvFile(null);
        fetchPicks();
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || error.message || 'Failed to upload picks');
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      console.error('File reading error');
      toast.error('Failed to read the CSV file');
      setUploading(false);
    };

    reader.readAsText(csvFile);
  };

  const downloadTemplate = () => {
    // Full Intelligence Report CSV template
    const headers = [
      // Core Fields
      'Sport', 'Player Name', 'Prop Type', 'Line', 'Pick', 'Game', 'Opponent',
      // Reasoning/Analysis (basic)
      'Reasoning', 'Player Stats', 'Matchup Analysis', 'Confidence (1-10)',
      // Outlier Projection Data
      'Last 5 Avg', 'Last 10 Avg', 'Last 20 Avg', 'Season Avg', 'Season Median',
      'L5 Gap', 'L10 Gap', 'L20 Gap', 'Season Median Gap', 'Structural Cushion',
      // Structural Edge
      'Structural Edge Class', 'Structural Edge Analysis',
      // Trend Data
      'Last 5 Hit Rate', 'Last 5 Median', 'Last 10 Hit Rate', 'Last 10 Median',
      'Last 20 Hit Rate', 'Last 20 Median', 'Season Hit Rate',
      // Head-to-Head
      'H2H Hit Rate', 'H2H Outcomes', 'H2H Avg', 'H2H Median', 'H2H Analysis',
      // Matchup Context
      'Opp Pts Allowed', 'Opp Pts Rank', 'Opp Reb Allowed', 'Opp Reb Rank',
      'Opp Ast Allowed', 'Opp Ast Rank', 'Matchup Interpretation',
      // Pace
      'Opp Pace Rank', 'Pace Analysis',
      // Defense vs Position
      'DVP Pts', 'DVP Reb', 'DVP Ast', 'DVP Overall',
      // Supporting Metrics
      'Potential Assists', 'Rebound Chances', 'Projected Minutes', 'FG Attempts',
      // Public Betting
      'Public Bet Pct', 'Public Money Pct', 'Betting Interpretation',
      // Edge Summary
      'Edge Signals', 'Investigative Analysis',
      // Decision Card
      'Grade', 'Action', 'Action Reason', 'Distribution Interpretation',
      'Opportunity Signals', 'Ceiling Potential', 'Risk Conditions', 'Kill Switch',
      // PSL Score
      'PSL Prop Score', 'Score Explanation',
      // Heat Meter
      'Heat Level', 'Heat Score Range',
      // Executive Summary
      'Slate Type', 'Slate Size', 'Overall Confidence', 'Risk Meter', 'Approval Status',
      // Settings
      'Featured (true/false)', 'Published (true/false)'
    ];
    
    // Example row with all fields
    const exampleRow = [
      'NBA', 'Kevin Porter Jr.', 'Points + Rebounds + Assists', '24.5', 'Over 24.5 PRA', 'MIL vs ATL', 'Atlanta Hawks',
      'Elite structural edge with season median clearing line by 8.5', 'L5: 28 | L10: 30.7 | L20: 28.9 | Season: 30.03', 'ATL ranks 27th vs guards in points allowed', '8',
      '28', '30.7', '28.9', '30.03', '33',
      '+3.5', '+6.2', '+4.4', '+8.5', 'Season median of 33 PRA clears the 24.5 line by 8.5 points',
      'Elite Structural Edge', 'A median clearing the line by more than eight points indicates strong distribution',
      '60% (3/5)', '27', '70% (7/10)', '33', '70% (14/20)', '30.5', '71%',
      '50% (2 of 4)', '25, 19, 37, 22', '25.75', '23.5', 'Mixed results - limited predictive weight',
      '26.8', '27', '7.5', '23', '5.6', '17', 'Atlanta ranks bottom tier defensively against guards',
      'Top 5 in NBA', 'High pace increases total possessions',
      'Favorable', 'Favorable', 'Neutral', 'Favorable',
      '14.4', '9.9', '33', '13-14',
      '58%', '77%', 'Money percentage exceeding bet percentage suggests sharper market positioning',
      'Season median +8.5 | L10 & L20 70% hit rate | Bottom-tier defense vs guards', 'Two low scoring games in L5 | High pace environment | 14+ potential assists',
      'A-', 'PLAY', 'Elite structural edge with strong hit rates', 'Most outcomes fall above market line (30-33 PRA)',
      'Minutes and potential assists suggest stable opportunity', '40+ PRA ceiling outcomes', 'Low scoring games can suppress PRA', 'Minutes below 28 | Late lineup changes | Line above 28.5',
      '89', 'Elite structural edge + strong hit rates + favorable matchup',
      'Strong Edge', '80-89',
      'NBA', 'Single Prop', 'High', 'Moderate', 'APPROVED',
      'false', 'true'
    ];
    
    const template = headers.join(',') + '\n' + exampleRow.map(v => `"${v}"`).join(',');
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'psl_intelligence_report_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleEdit = (pick) => {
    setEditingPick(pick);
    // Copy ALL fields from the pick to avoid data bleeding between edits
    setFormData({
      sport: pick.sport || 'NBA',
      player_name: pick.player_name || '',
      prop_type: pick.prop_type || '',
      line: pick.line || '',
      pick: pick.pick || '',
      reasoning: pick.reasoning || '',
      player_stats: pick.player_stats || '',
      matchup_analysis: pick.matchup_analysis || '',
      confidence_level: pick.confidence_level || 7,
      youtube_url: pick.youtube_url || '',
      thumbnail_url: pick.thumbnail_url || '',
      featured: pick.featured || false,
      published: pick.published !== undefined ? pick.published : true,
      affiliate_links: pick.affiliate_links || [],
      // Intelligence Report Fields - MUST copy these to avoid data bleeding
      game: pick.game || '',
      opponent: pick.opponent || '',
      last_5_avg: pick.last_5_avg || '',
      last_10_avg: pick.last_10_avg || '',
      last_20_avg: pick.last_20_avg || '',
      season_avg: pick.season_avg || '',
      season_median: pick.season_median || '',
      l5_gap: pick.l5_gap || '',
      l10_gap: pick.l10_gap || '',
      l20_gap: pick.l20_gap || '',
      season_median_gap: pick.season_median_gap || '',
      median_vs_line_gap: pick.median_vs_line_gap || '',
      structural_cushion: pick.structural_cushion || '',
      structural_edge_class: pick.structural_edge_class || '',
      structural_edge_analysis: pick.structural_edge_analysis || '',
      last_5_hit_rate: pick.last_5_hit_rate || '',
      last_5_median: pick.last_5_median || '',
      last_10_hit_rate: pick.last_10_hit_rate || '',
      last_10_median: pick.last_10_median || '',
      last_20_hit_rate: pick.last_20_hit_rate || '',
      last_20_median: pick.last_20_median || '',
      season_hit_rate: pick.season_hit_rate || '',
      h2h_hit_rate: pick.h2h_hit_rate || '',
      h2h_outcomes: pick.h2h_outcomes || '',
      h2h_avg: pick.h2h_avg || '',
      h2h_median: pick.h2h_median || '',
      h2h_analysis: pick.h2h_analysis || '',
      opp_pts_allowed: pick.opp_pts_allowed || '',
      opp_pts_rank: pick.opp_pts_rank || '',
      opp_reb_allowed: pick.opp_reb_allowed || '',
      opp_reb_rank: pick.opp_reb_rank || '',
      opp_ast_allowed: pick.opp_ast_allowed || '',
      opp_ast_rank: pick.opp_ast_rank || '',
      matchup_interpretation: pick.matchup_interpretation || '',
      opp_pace_rank: pick.opp_pace_rank || '',
      pace_analysis: pick.pace_analysis || '',
      dvp_pts: pick.dvp_pts || '',
      dvp_reb: pick.dvp_reb || '',
      dvp_ast: pick.dvp_ast || '',
      dvp_overall: pick.dvp_overall || '',
      potential_assists: pick.potential_assists || '',
      rebound_chances: pick.rebound_chances || '',
      projected_minutes: pick.projected_minutes || '',
      fg_attempts: pick.fg_attempts || '',
      public_bet_pct: pick.public_bet_pct || '',
      public_money_pct: pick.public_money_pct || '',
      betting_interpretation: pick.betting_interpretation || '',
      edge_signals: pick.edge_signals || '',
      investigative_analysis: pick.investigative_analysis || '',
      grade: pick.grade || '',
      action: pick.action || '',
      action_reason: pick.action_reason || '',
      distribution_interpretation: pick.distribution_interpretation || '',
      opportunity_signals: pick.opportunity_signals || '',
      ceiling_potential: pick.ceiling_potential || '',
      risk_conditions: pick.risk_conditions || '',
      kill_switch: pick.kill_switch || '',
      psl_prop_score: pick.psl_prop_score || '',
      score_explanation: pick.score_explanation || '',
      heat_level: pick.heat_level || '',
      heat_score_range: pick.heat_score_range || '',
      expected_range: pick.expected_range || '',
      median_projection: pick.median_projection || '',
      clearance_probability_estimate: pick.clearance_probability_estimate || '',
      slate_type: pick.slate_type || '',
      slate_size: pick.slate_size || '',
      overall_confidence: pick.overall_confidence || '',
      risk_meter: pick.risk_meter || '',
      approval_status: pick.approval_status || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPick(null);
    setFormData({
      sport: 'NBA',
      player_name: '',
      prop_type: '',
      line: '',
      pick: '',
      reasoning: '',
      player_stats: '',
      matchup_analysis: '',
      confidence_level: 7,
      youtube_url: '',
      thumbnail_url: '',
      featured: false,
      published: true,
      affiliate_links: [],
      // Intelligence Report Fields
      game: '',
      opponent: '',
      last_5_avg: '',
      last_10_avg: '',
      last_20_avg: '',
      season_avg: '',
      season_median: '',
      l5_gap: '',
      l10_gap: '',
      l20_gap: '',
      season_median_gap: '',
      median_vs_line_gap: '',
      structural_cushion: '',
      structural_edge_class: '',
      structural_edge_analysis: '',
      last_5_hit_rate: '',
      last_5_median: '',
      last_10_hit_rate: '',
      last_10_median: '',
      last_20_hit_rate: '',
      last_20_median: '',
      season_hit_rate: '',
      h2h_hit_rate: '',
      h2h_outcomes: '',
      h2h_avg: '',
      h2h_median: '',
      h2h_analysis: '',
      opp_pts_allowed: '',
      opp_pts_rank: '',
      opp_reb_allowed: '',
      opp_reb_rank: '',
      opp_ast_allowed: '',
      opp_ast_rank: '',
      matchup_interpretation: '',
      opp_pace_rank: '',
      pace_analysis: '',
      dvp_pts: '',
      dvp_reb: '',
      dvp_ast: '',
      dvp_overall: '',
      potential_assists: '',
      rebound_chances: '',
      projected_minutes: '',
      fg_attempts: '',
      public_bet_pct: '',
      public_money_pct: '',
      betting_interpretation: '',
      edge_signals: '',
      investigative_analysis: '',
      grade: '',
      action: '',
      action_reason: '',
      distribution_interpretation: '',
      opportunity_signals: '',
      ceiling_potential: '',
      risk_conditions: '',
      kill_switch: '',
      psl_prop_score: '',
      score_explanation: '',
      heat_level: '',
      heat_score_range: '',
      expected_range: '',
      median_projection: '',
      clearance_probability_estimate: '',
      slate_type: '',
      slate_size: '',
      overall_confidence: '',
      risk_meter: '',
      approval_status: ''
    });
    setAffiliateInput({ text: '', url: '' });
  };

  const addAffiliateLink = () => {
    if (affiliateInput.text && affiliateInput.url) {
      setFormData({
        ...formData,
        affiliate_links: [...formData.affiliate_links, affiliateInput]
      });
      setAffiliateInput({ text: '', url: '' });
    }
  };

  const removeAffiliateLink = (index) => {
    setFormData({
      ...formData,
      affiliate_links: formData.affiliate_links.filter((_, i) => i !== index)
    });
  };

  const updateResult = async (pickId, result, outcome) => {
    try {
      await axios.put(`${API}/picks/${pickId}`, { result, outcome }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Result updated!');
      fetchPicks();
      fetchResultsSummary();
    } catch (error) {
      toast.error('Failed to update result');
    }
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="admin-dashboard">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-heading font-bold mb-2">ADMIN DASHBOARD</h1>
            <p className="text-muted-foreground">Manage your picks, articles, and content</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/admin/command-center')}
              className="bg-primary text-white"
            >
              Command Center
            </Button>
            <Button
              onClick={() => navigate('/admin/seo')}
              variant="outline"
            >
              SEO Dashboard
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-border pb-4">
          <button
            onClick={() => setActiveTab('picks')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'picks' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-picks"
          >
            <Pencil size={16} />
            Picks ({picks.length})
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'articles' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-articles"
          >
            <FileText size={16} />
            Articles ({articles.length})
          </button>
          <button
            onClick={() => setActiveTab('challenge')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'challenge' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-challenge"
          >
            <Swords size={16} />
            Challengers ({challengeEntries.length})
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'support' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-support"
          >
            <Bell size={16} />
            Support {supportNewCount > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold">{supportNewCount}</span>}
          </button>
        </div>

        {/* Tab Navigation - Subscribers tab hidden for now, can be re-enabled later */}
        {/* 
        <div className="flex gap-2 mb-8 border-b border-border pb-4">
          <button
            onClick={() => setActiveTab('picks')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'picks' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-picks"
          >
            <Pencil size={16} />
            Picks ({picks.length})
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all ${
              activeTab === 'subscribers' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-subscribers"
          >
            <Users size={16} />
            Subscribers ({subscribers.length})
          </button>
        </div>
        */}

        {activeTab === 'picks' && (
          <>
        {/* Record Summary */}
        {resultsSummary && (
          <div className="mb-6 p-4 bg-surface rounded border border-border flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="font-mono text-sm text-muted-foreground">RECORD:</span>
              <span className="text-accent font-bold">{resultsSummary.wins}W</span>
              <span className="text-red-500 font-bold">{resultsSummary.losses}L</span>
              <span className="text-yellow-500 font-bold">{resultsSummary.pushes}P</span>
              <span className="text-primary font-bold">{resultsSummary.win_percentage}%</span>
            </div>
            <Button
              onClick={handleAutoUpdateResults}
              disabled={autoUpdating}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
              data-testid="auto-update-results"
            >
              {autoUpdating ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  <Zap className="mr-2" size={16} />
                  Auto-Update Results
                </>
              )}
            </Button>
            <Button
              onClick={handleBulkRegenThumbnails}
              disabled={!!regenProgress}
              variant="outline"
              className="font-bold ml-2"
              data-testid="regen-all-thumbnails"
            >
              {regenProgress ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  {regenProgress.current}/{regenProgress.total}
                </>
              ) : (
                <>
                  <Image className="mr-2" size={16} />
                  Regen All Thumbnails
                </>
              )}
            </Button>
          </div>
        )}
        
        <div className="flex gap-3 mb-6">
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-secondary text-white hover:bg-secondary/90 font-bold uppercase tracking-wider"
                  data-testid="bulk-upload-button"
                >
                  <Upload className="mr-2" size={16} />
                  Bulk Upload CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading">BULK UPLOAD PICKS</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-surface-highlight p-4 rounded border border-border">
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload multiple picks at once using a CSV file. Download the template or format guide below.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={downloadTemplate}
                        variant="outline"
                        size="sm"
                        data-testid="download-template"
                      >
                        <Download className="mr-2" size={14} />
                        Download CSV Template
                      </Button>
                      <a
                        href="/csv-template.csv"
                        download="primestatlab-csv-template.csv"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                      >
                        <FileText className="mr-2" size={14} />
                        Simple Template (3 examples)
                      </a>
                    </div>
                  </div>
                  
                  <form onSubmit={handleCsvUpload} className="space-y-4">
                    <div>
                      <Label htmlFor="csv-file" className="font-mono text-xs uppercase tracking-widest">Select CSV File</Label>
                      <Input
                        id="csv-file"
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files[0])}
                        className="bg-surface border-border"
                        data-testid="csv-file-input"
                      />
                    </div>
                    
                    <div className="bg-surface-highlight p-4 rounded">
                      <p className="text-xs font-mono text-muted-foreground mb-2">CSV Format Example:</p>
                      <pre className="text-xs bg-surface p-2 rounded overflow-x-auto">
                        Sport,Player Name,Prop Type,Line,Pick,Reasoning,...
                      </pre>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setUploadDialogOpen(false);
                          setCsvFile(null);
                        }}
                        data-testid="cancel-upload"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider"
                        disabled={uploading || !csvFile}
                        data-testid="upload-csv"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="animate-spin mr-2" size={16} />
                            Uploading...
                          </>
                        ) : (
                          'Upload Picks'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider"
                  data-testid="create-pick-button"
                >
                  <Plus className="mr-2" size={16} />
                  New Pick
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading">
                  {editingPick ? 'EDIT PICK' : 'CREATE NEW PICK'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sport" className="font-mono text-xs uppercase tracking-widest">Sport</Label>
                    <Input
                      id="sport"
                      value={formData.sport}
                      onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                      required
                      data-testid="sport-input"
                      className="bg-surface border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="player_name" className="font-mono text-xs uppercase tracking-widest">Player Name</Label>
                    <Input
                      id="player_name"
                      value={formData.player_name}
                      onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                      required
                      data-testid="player-name-input"
                      className="bg-surface border-border"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prop_type" className="font-mono text-xs uppercase tracking-widest">Prop Type</Label>
                    <Input
                      id="prop_type"
                      value={formData.prop_type}
                      onChange={(e) => setFormData({ ...formData, prop_type: e.target.value })}
                      placeholder="Points, Rebounds, Assists, etc."
                      required
                      data-testid="prop-type-input"
                      className="bg-surface border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="line" className="font-mono text-xs uppercase tracking-widest">Line</Label>
                    <Input
                      id="line"
                      value={formData.line}
                      onChange={(e) => setFormData({ ...formData, line: e.target.value })}
                      placeholder="25.5"
                      required
                      data-testid="line-input"
                      className="bg-surface border-border"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pick" className="font-mono text-xs uppercase tracking-widest">Pick</Label>
                  <Input
                    id="pick"
                    value={formData.pick}
                    onChange={(e) => setFormData({ ...formData, pick: e.target.value })}
                    placeholder="Over 25.5"
                    required
                    data-testid="pick-input"
                    className="bg-surface border-border"
                  />
                </div>
                <div>
                  <Label htmlFor="reasoning" className="font-mono text-xs uppercase tracking-widest">Reasoning</Label>
                  <Textarea
                    id="reasoning"
                    value={formData.reasoning}
                    onChange={(e) => setFormData({ ...formData, reasoning: e.target.value })}
                    required
                    rows={4}
                    data-testid="reasoning-input"
                    className="bg-surface border-border"
                  />
                </div>
                <div>
                  <Label htmlFor="player_stats" className="font-mono text-xs uppercase tracking-widest">Player Stats</Label>
                  <Textarea
                    id="player_stats"
                    value={formData.player_stats}
                    onChange={(e) => setFormData({ ...formData, player_stats: e.target.value })}
                    rows={3}
                    placeholder="Last 10 games average, season stats, etc."
                    data-testid="player-stats-input"
                    className="bg-surface border-border"
                  />
                </div>
                <div>
                  <Label htmlFor="matchup_analysis" className="font-mono text-xs uppercase tracking-widest">Matchup Analysis</Label>
                  <Textarea
                    id="matchup_analysis"
                    value={formData.matchup_analysis}
                    onChange={(e) => setFormData({ ...formData, matchup_analysis: e.target.value })}
                    rows={3}
                    placeholder="Opponent defense ranking, pace, etc."
                    data-testid="matchup-input"
                    className="bg-surface border-border"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="confidence_level" className="font-mono text-xs uppercase tracking-widest">Confidence Level (1-10)</Label>
                    <Input
                      id="confidence_level"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.confidence_level}
                      onChange={(e) => setFormData({ ...formData, confidence_level: parseInt(e.target.value) })}
                      required
                      data-testid="confidence-input"
                      className="bg-surface border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="youtube_url" className="font-mono text-xs uppercase tracking-widest">YouTube URL (Optional)</Label>
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                      placeholder="https://youtube.com/..."
                      data-testid="youtube-input"
                      className="bg-surface border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="thumbnail_url" className="font-mono text-xs uppercase tracking-widest">Thumbnail Image</Label>
                    <div className="flex gap-2 mb-2">
                      <label className="flex-1">
                        <div className={`flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-border rounded cursor-pointer hover:border-primary transition-colors ${uploadingThumbnail ? 'opacity-50' : ''}`}>
                          {uploadingThumbnail ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            <Upload size={16} />
                          )}
                          <span className="text-sm">{uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          className="hidden"
                          disabled={uploadingThumbnail}
                        />
                      </label>
                    </div>
                    <Input
                      id="thumbnail_url"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      placeholder="Or paste image URL here..."
                      data-testid="thumbnail-input"
                      className="bg-surface border-border"
                    />
                    {formData.thumbnail_url && (
                      <div className="mt-2 relative inline-block">
                        <img 
                          src={formData.thumbnail_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.thumbnail_url}` : formData.thumbnail_url} 
                          alt="Thumbnail preview" 
                          className="h-24 w-auto rounded border border-border"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, thumbnail_url: '' })}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      data-testid="featured-checkbox"
                    />
                    <span className="font-mono text-xs uppercase tracking-widest">Featured Pick</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                      data-testid="published-checkbox"
                    />
                    <span className="font-mono text-xs uppercase tracking-widest">Published</span>
                  </label>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-mono text-xs uppercase tracking-widest mb-3">Affiliate Links</h3>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Link Text"
                      value={affiliateInput.text}
                      onChange={(e) => setAffiliateInput({ ...affiliateInput, text: e.target.value })}
                      data-testid="affiliate-text-input"
                      className="bg-surface border-border"
                    />
                    <Input
                      placeholder="URL"
                      value={affiliateInput.url}
                      onChange={(e) => setAffiliateInput({ ...affiliateInput, url: e.target.value })}
                      data-testid="affiliate-url-input"
                      className="bg-surface border-border"
                    />
                    <Button type="button" onClick={addAffiliateLink} data-testid="add-affiliate-button">Add</Button>
                  </div>
                  <div className="space-y-2">
                    {formData.affiliate_links.map((link, index) => (
                      <div key={index} className="flex items-center justify-between bg-surface p-2 text-sm" data-testid={`affiliate-${index}`}>
                        <span>{link.text}: {link.url}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAffiliateLink(index)}
                          data-testid={`remove-affiliate-${index}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ========== INTELLIGENCE REPORT FIELDS ========== */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="font-heading text-lg font-bold mb-4 text-primary">LINE INTELLIGENCE REPORT DATA</h3>
                  
                  {/* PROP HEADER */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Prop Header</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-widest">Game</Label>
                        <Input
                          value={formData.game}
                          onChange={(e) => setFormData({ ...formData, game: e.target.value })}
                          placeholder="MIL vs ATL"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-widest">Opponent</Label>
                        <Input
                          value={formData.opponent}
                          onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
                          placeholder="Atlanta Hawks"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* OUTLIER PROJECTION DATA */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Outlier Projection Data</h4>
                    <div className="grid grid-cols-5 gap-3 mb-4">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Last 5 Avg</Label>
                        <Input
                          value={formData.last_5_avg}
                          onChange={(e) => setFormData({ ...formData, last_5_avg: e.target.value })}
                          placeholder="28"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Last 10 Avg</Label>
                        <Input
                          value={formData.last_10_avg}
                          onChange={(e) => setFormData({ ...formData, last_10_avg: e.target.value })}
                          placeholder="30.7"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Last 20 Avg</Label>
                        <Input
                          value={formData.last_20_avg}
                          onChange={(e) => setFormData({ ...formData, last_20_avg: e.target.value })}
                          placeholder="28.9"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Season Avg</Label>
                        <Input
                          value={formData.season_avg}
                          onChange={(e) => setFormData({ ...formData, season_avg: e.target.value })}
                          placeholder="30.03"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Season Median</Label>
                        <Input
                          value={formData.season_median}
                          onChange={(e) => setFormData({ ...formData, season_median: e.target.value })}
                          placeholder="33"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L5 Gap</Label>
                        <Input
                          value={formData.l5_gap}
                          onChange={(e) => setFormData({ ...formData, l5_gap: e.target.value })}
                          placeholder="+3.5"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L10 Gap</Label>
                        <Input
                          value={formData.l10_gap}
                          onChange={(e) => setFormData({ ...formData, l10_gap: e.target.value })}
                          placeholder="+6.2"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L20 Gap</Label>
                        <Input
                          value={formData.l20_gap}
                          onChange={(e) => setFormData({ ...formData, l20_gap: e.target.value })}
                          placeholder="+4.4"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Season Median Gap</Label>
                        <Input
                          value={formData.season_median_gap}
                          onChange={(e) => setFormData({ ...formData, season_median_gap: e.target.value })}
                          placeholder="+8.5"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Median vs Line Gap</Label>
                        <Input
                          value={formData.median_vs_line_gap}
                          onChange={(e) => setFormData({ ...formData, median_vs_line_gap: e.target.value })}
                          placeholder="+4.0"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase">Structural Cushion Assessment</Label>
                      <Textarea
                        value={formData.structural_cushion}
                        onChange={(e) => setFormData({ ...formData, structural_cushion: e.target.value })}
                        placeholder="Season median of 33 PRA clears the 24.5 line by 8.5 points..."
                        rows={2}
                        className="bg-surface border-border"
                      />
                    </div>
                  </div>

                  {/* STRUCTURAL EDGE */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Structural Edge Detector</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Edge Classification</Label>
                        <Input
                          value={formData.structural_edge_class}
                          onChange={(e) => setFormData({ ...formData, structural_edge_class: e.target.value })}
                          placeholder="Elite Structural Edge"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase">Edge Analysis</Label>
                      <Textarea
                        value={formData.structural_edge_analysis}
                        onChange={(e) => setFormData({ ...formData, structural_edge_analysis: e.target.value })}
                        placeholder="A median clearing the line by more than eight points indicates..."
                        rows={2}
                        className="bg-surface border-border"
                      />
                    </div>
                  </div>

                  {/* TREND DATA */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Trend Data (Hit Rates)</h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L5 Hit Rate</Label>
                        <Input
                          value={formData.last_5_hit_rate}
                          onChange={(e) => setFormData({ ...formData, last_5_hit_rate: e.target.value })}
                          placeholder="60% (3/5)"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L10 Hit Rate</Label>
                        <Input
                          value={formData.last_10_hit_rate}
                          onChange={(e) => setFormData({ ...formData, last_10_hit_rate: e.target.value })}
                          placeholder="70% (7/10)"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">L20 Hit Rate</Label>
                        <Input
                          value={formData.last_20_hit_rate}
                          onChange={(e) => setFormData({ ...formData, last_20_hit_rate: e.target.value })}
                          placeholder="70% (14/20)"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Season Hit Rate</Label>
                        <Input
                          value={formData.season_hit_rate}
                          onChange={(e) => setFormData({ ...formData, season_hit_rate: e.target.value })}
                          placeholder="71%"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* HEAD-TO-HEAD */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Head-to-Head Analysis</h4>
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">H2H Hit Rate</Label>
                        <Input
                          value={formData.h2h_hit_rate}
                          onChange={(e) => setFormData({ ...formData, h2h_hit_rate: e.target.value })}
                          placeholder="50% (2 of 4)"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">H2H Outcomes</Label>
                        <Input
                          value={formData.h2h_outcomes}
                          onChange={(e) => setFormData({ ...formData, h2h_outcomes: e.target.value })}
                          placeholder="25, 19, 37, 22"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">H2H Avg</Label>
                        <Input
                          value={formData.h2h_avg}
                          onChange={(e) => setFormData({ ...formData, h2h_avg: e.target.value })}
                          placeholder="25.75"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">H2H Median</Label>
                        <Input
                          value={formData.h2h_median}
                          onChange={(e) => setFormData({ ...formData, h2h_median: e.target.value })}
                          placeholder="23.5"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase">H2H Analysis</Label>
                      <Textarea
                        value={formData.h2h_analysis}
                        onChange={(e) => setFormData({ ...formData, h2h_analysis: e.target.value })}
                        placeholder="Mixed results - limited predictive weight..."
                        rows={2}
                        className="bg-surface border-border"
                      />
                    </div>
                  </div>

                  {/* MATCHUP CONTEXT */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Matchup Context</h4>
                    <div className="grid grid-cols-6 gap-3 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Opp Pts Allowed</Label>
                        <Input
                          value={formData.opp_pts_allowed}
                          onChange={(e) => setFormData({ ...formData, opp_pts_allowed: e.target.value })}
                          placeholder="26.8"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Pts Rank</Label>
                        <Input
                          value={formData.opp_pts_rank}
                          onChange={(e) => setFormData({ ...formData, opp_pts_rank: e.target.value })}
                          placeholder="27"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Opp Reb Allowed</Label>
                        <Input
                          value={formData.opp_reb_allowed}
                          onChange={(e) => setFormData({ ...formData, opp_reb_allowed: e.target.value })}
                          placeholder="7.5"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Reb Rank</Label>
                        <Input
                          value={formData.opp_reb_rank}
                          onChange={(e) => setFormData({ ...formData, opp_reb_rank: e.target.value })}
                          placeholder="23"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Opp Ast Allowed</Label>
                        <Input
                          value={formData.opp_ast_allowed}
                          onChange={(e) => setFormData({ ...formData, opp_ast_allowed: e.target.value })}
                          placeholder="5.6"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Ast Rank</Label>
                        <Input
                          value={formData.opp_ast_rank}
                          onChange={(e) => setFormData({ ...formData, opp_ast_rank: e.target.value })}
                          placeholder="17"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase">Matchup Interpretation</Label>
                      <Textarea
                        value={formData.matchup_interpretation}
                        onChange={(e) => setFormData({ ...formData, matchup_interpretation: e.target.value })}
                        placeholder="Atlanta ranks bottom tier defensively against guards..."
                        rows={2}
                        className="bg-surface border-border"
                      />
                    </div>
                  </div>

                  {/* PACE & DVP */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Pace & Defense vs Position</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Opp Pace Rank</Label>
                        <Input
                          value={formData.opp_pace_rank}
                          onChange={(e) => setFormData({ ...formData, opp_pace_rank: e.target.value })}
                          placeholder="Top 5 in NBA"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Pace Analysis</Label>
                        <Input
                          value={formData.pace_analysis}
                          onChange={(e) => setFormData({ ...formData, pace_analysis: e.target.value })}
                          placeholder="High pace increases total possessions"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">DVP Pts</Label>
                        <Input
                          value={formData.dvp_pts}
                          onChange={(e) => setFormData({ ...formData, dvp_pts: e.target.value })}
                          placeholder="Favorable"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">DVP Reb</Label>
                        <Input
                          value={formData.dvp_reb}
                          onChange={(e) => setFormData({ ...formData, dvp_reb: e.target.value })}
                          placeholder="Favorable"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">DVP Ast</Label>
                        <Input
                          value={formData.dvp_ast}
                          onChange={(e) => setFormData({ ...formData, dvp_ast: e.target.value })}
                          placeholder="Neutral"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">DVP Overall</Label>
                        <Input
                          value={formData.dvp_overall}
                          onChange={(e) => setFormData({ ...formData, dvp_overall: e.target.value })}
                          placeholder="Favorable"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* SUPPORTING METRICS */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Supporting Opportunity Metrics</h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Potential Assists</Label>
                        <Input
                          value={formData.potential_assists}
                          onChange={(e) => setFormData({ ...formData, potential_assists: e.target.value })}
                          placeholder="14.4"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Rebound Chances</Label>
                        <Input
                          value={formData.rebound_chances}
                          onChange={(e) => setFormData({ ...formData, rebound_chances: e.target.value })}
                          placeholder="9.9"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Projected Minutes</Label>
                        <Input
                          value={formData.projected_minutes}
                          onChange={(e) => setFormData({ ...formData, projected_minutes: e.target.value })}
                          placeholder="33"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">FG Attempts</Label>
                        <Input
                          value={formData.fg_attempts}
                          onChange={(e) => setFormData({ ...formData, fg_attempts: e.target.value })}
                          placeholder="13-14"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* PUBLIC BETTING */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Public Betting Data</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Public Bet %</Label>
                        <Input
                          value={formData.public_bet_pct}
                          onChange={(e) => setFormData({ ...formData, public_bet_pct: e.target.value })}
                          placeholder="58%"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Public Money %</Label>
                        <Input
                          value={formData.public_money_pct}
                          onChange={(e) => setFormData({ ...formData, public_money_pct: e.target.value })}
                          placeholder="77%"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Betting Interpretation</Label>
                        <Input
                          value={formData.betting_interpretation}
                          onChange={(e) => setFormData({ ...formData, betting_interpretation: e.target.value })}
                          placeholder="Sharp money backing the over"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* EDGE SUMMARY */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Edge Summary & Investigation</h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Key Edge Signals</Label>
                        <Textarea
                          value={formData.edge_signals}
                          onChange={(e) => setFormData({ ...formData, edge_signals: e.target.value })}
                          placeholder="Season median +8.5 | L10 & L20 70% hit rate | Bottom-tier defense vs guards"
                          rows={2}
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Investigative Analysis (What Are We Missing)</Label>
                        <Textarea
                          value={formData.investigative_analysis}
                          onChange={(e) => setFormData({ ...formData, investigative_analysis: e.target.value })}
                          placeholder="Two low scoring games in L5 | High pace environment | 14+ potential assists"
                          rows={2}
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* DECISION CARD */}
                  <div className="mb-6 p-4 bg-primary/5 rounded border border-primary/20">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-primary">Operator Decision Card</h4>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Grade</Label>
                        <Input
                          value={formData.grade}
                          onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                          placeholder="A-"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Action</Label>
                        <Input
                          value={formData.action}
                          onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                          placeholder="PLAY"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Action Reason</Label>
                        <Input
                          value={formData.action_reason}
                          onChange={(e) => setFormData({ ...formData, action_reason: e.target.value })}
                          placeholder="Elite structural edge with strong hit rates"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Distribution Interpretation</Label>
                        <Input
                          value={formData.distribution_interpretation}
                          onChange={(e) => setFormData({ ...formData, distribution_interpretation: e.target.value })}
                          placeholder="Most outcomes fall above market line (30-33 PRA)"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Opportunity Signals</Label>
                        <Input
                          value={formData.opportunity_signals}
                          onChange={(e) => setFormData({ ...formData, opportunity_signals: e.target.value })}
                          placeholder="Minutes and potential assists suggest stable opportunity"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Ceiling Potential</Label>
                        <Input
                          value={formData.ceiling_potential}
                          onChange={(e) => setFormData({ ...formData, ceiling_potential: e.target.value })}
                          placeholder="40+ PRA ceiling outcomes"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Risk Conditions</Label>
                        <Input
                          value={formData.risk_conditions}
                          onChange={(e) => setFormData({ ...formData, risk_conditions: e.target.value })}
                          placeholder="Low scoring games can suppress PRA"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase">Kill Switch</Label>
                      <Input
                        value={formData.kill_switch}
                        onChange={(e) => setFormData({ ...formData, kill_switch: e.target.value })}
                        placeholder="Minutes below 28 | Late lineup changes | Line above 28.5"
                        className="bg-surface border-border"
                      />
                    </div>
                  </div>

                  {/* PSL SCORE & HEAT METER */}
                  <div className="mb-6 p-4 bg-green-500/5 rounded border border-green-500/20">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-green-500">PSL Prop Score & Heat Meter</h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">PSL Prop Score (0-100)</Label>
                        <Input
                          value={formData.psl_prop_score}
                          onChange={(e) => setFormData({ ...formData, psl_prop_score: e.target.value })}
                          placeholder="89"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Score Explanation</Label>
                        <Input
                          value={formData.score_explanation}
                          onChange={(e) => setFormData({ ...formData, score_explanation: e.target.value })}
                          placeholder="Elite structural edge + strong hit rates"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Heat Level</Label>
                        <Input
                          value={formData.heat_level}
                          onChange={(e) => setFormData({ ...formData, heat_level: e.target.value })}
                          placeholder="Strong Edge"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Heat Score Range</Label>
                        <Input
                          value={formData.heat_score_range}
                          onChange={(e) => setFormData({ ...formData, heat_score_range: e.target.value })}
                          placeholder="80-89"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* DISTRIBUTION OUTLOOK */}
                  <div className="mb-6 p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-blue-400">Distribution Outlook</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Expected Range</Label>
                        <Input
                          value={formData.expected_range}
                          onChange={(e) => setFormData({ ...formData, expected_range: e.target.value })}
                          placeholder="7-11 RA"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Median Projection</Label>
                        <Input
                          value={formData.median_projection}
                          onChange={(e) => setFormData({ ...formData, median_projection: e.target.value })}
                          placeholder="8.5"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Clearance Probability</Label>
                        <Input
                          value={formData.clearance_probability_estimate}
                          onChange={(e) => setFormData({ ...formData, clearance_probability_estimate: e.target.value })}
                          placeholder="72-76%"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>

                  {/* EXECUTIVE SUMMARY */}
                  <div className="p-4 bg-surface/50 rounded border border-border">
                    <h4 className="font-mono text-xs uppercase tracking-widest mb-3 text-muted-foreground">Executive Summary</h4>
                    <div className="grid grid-cols-5 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Slate Type</Label>
                        <Input
                          value={formData.slate_type}
                          onChange={(e) => setFormData({ ...formData, slate_type: e.target.value })}
                          placeholder="NBA"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Slate Size</Label>
                        <Input
                          value={formData.slate_size}
                          onChange={(e) => setFormData({ ...formData, slate_size: e.target.value })}
                          placeholder="Single Prop"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Overall Confidence</Label>
                        <Input
                          value={formData.overall_confidence}
                          onChange={(e) => setFormData({ ...formData, overall_confidence: e.target.value })}
                          placeholder="High"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Risk Meter</Label>
                        <Input
                          value={formData.risk_meter}
                          onChange={(e) => setFormData({ ...formData, risk_meter: e.target.value })}
                          placeholder="Moderate"
                          className="bg-surface border-border"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase">Approval Status</Label>
                        <Input
                          value={formData.approval_status}
                          onChange={(e) => setFormData({ ...formData, approval_status: e.target.value })}
                          placeholder="APPROVED"
                          className="bg-surface border-border"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider"
                    disabled={loading}
                    data-testid="save-pick-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Saving...
                      </>
                    ) : (
                      <>{editingPick ? 'Update Pick' : 'Create Pick'}</>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
            <Loader2 className="animate-spin" size={40} />
          </div>
        ) : picks.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No picks yet. Create your first pick!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {picks.map((pick) => (
              <Card key={pick.id} className="border-border bg-card" data-testid={`pick-item-${pick.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-xs uppercase px-2 py-1 border border-primary text-primary">{pick.sport}</span>
                        {pick.featured && (
                          <span className="font-mono text-xs uppercase px-2 py-1 bg-primary text-white">Featured</span>
                        )}
                        <span className={`font-mono text-xs uppercase ${pick.published ? 'text-accent' : 'text-push'}`}>
                          {pick.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <CardTitle className="font-heading text-xl mb-2">{pick.player_name} - {pick.prop_type}</CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground font-mono">
                        <span>LINE: {pick.line}</span>
                        <span>PICK: {pick.pick}</span>
                        <span>CONFIDENCE: {pick.confidence_level}/10</span>
                      </div>
                      <div className="mt-3 flex items-center gap-4">
                        <select
                          value={pick.outcome || ''}
                          onChange={(e) => {
                            const outcome = e.target.value;
                            if (outcome) {
                              updateResult(pick.id, outcome.toUpperCase(), outcome);
                            }
                          }}
                          className="bg-surface border border-border px-3 py-1 text-sm font-mono rounded-sm"
                          data-testid={`outcome-select-${pick.id}`}
                        >
                          <option value="">Set Result</option>
                          <option value="win">Win</option>
                          <option value="loss">Loss</option>
                          <option value="push">Push</option>
                        </select>
                        {pick.result && (
                          <span className="font-mono text-sm">Result: {pick.result}</span>
                        )}
                        {/* Notify button hidden - can be re-enabled when email feature is needed
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendNotification(pick.id)}
                          className="ml-auto"
                          title="Send email notification to subscribers"
                          data-testid={`notify-pick-${pick.id}`}
                        >
                          <Bell size={14} className="mr-1" />
                          Notify
                        </Button>
                        */}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const stat = `${pick.pick} ${pick.line} ${pick.prop_type.replace(/Points/i,'PTS').replace(/Rebounds/i,'REB').replace(/Assists/i,'AST')}`;
                          const imageParam = pick.thumbnail_url ? encodeURIComponent(pick.thumbnail_url) : '';
                          const url = `/thumbnail?style=3&headline=FREE+PICKS&player=${encodeURIComponent(pick.player_name.toUpperCase())}&stat=${encodeURIComponent(stat)}&image=${imageParam}&effect=glow`;
                          window.open(url, '_blank');
                        }}
                        title="Open thumbnail generator with this pick's data"
                        data-testid={`regen-thumb-${pick.id}`}
                      >
                        <Image size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(pick)}
                        data-testid={`edit-pick-${pick.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(pick.id)}
                        data-testid={`delete-pick-${pick.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold">Manage Articles</h2>
                <p className="text-muted-foreground text-sm">Create and edit your articles</p>
              </div>
              <div className="flex gap-2">
                <div className="relative group">
                  <Button 
                    onClick={() => handleAutoGenerate('daily_breakdown')}
                    disabled={generatingContent}
                    variant="outline" 
                    className="font-mono text-xs uppercase tracking-wider gap-2"
                    data-testid="auto-generate-btn"
                  >
                    {generatingContent ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap size={14} />
                        Auto-Generate
                      </>
                    )}
                  </Button>
                </div>
              <Dialog open={articleDialogOpen} onOpenChange={(open) => {
                setArticleDialogOpen(open);
                if (!open) {
                  setEditingArticle(null);
                  setArticleFormData({ title: '', excerpt: '', content: '', featured_image: '', published: true });
                  setSuggestedSources([]);
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider" data-testid="new-article-button">
                    <Plus className="mr-2" size={16} />
                    New Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl bg-card border-border max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">
                      {editingArticle ? 'EDIT ARTICLE' : 'CREATE NEW ARTICLE'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleArticleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="article-title" className="font-mono text-xs uppercase tracking-widest">Title *</Label>
                      <Input
                        id="article-title"
                        value={articleFormData.title}
                        onChange={(e) => setArticleFormData({...articleFormData, title: e.target.value})}
                        required
                        className="bg-surface border-border"
                        data-testid="article-title-input"
                      />
                    </div>

                    {/* Featured Image Section */}
                    <div className="bg-surface p-4 rounded-lg border border-border">
                      <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">
                        <Image size={14} className="inline mr-2" />
                        Featured Image
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Upload an image or paste a URL
                      </p>
                      <div className="flex gap-3 mb-3">
                        <label className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded cursor-pointer hover:bg-primary/90 transition-colors">
                          {uploadingImage ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Upload size={16} />
                          )}
                          <span>{uploadingImage ? 'Uploading...' : 'Upload Image'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleArticleImageUpload}
                            disabled={uploadingImage}
                            data-testid="article-image-upload"
                          />
                        </label>
                        <span className="text-muted-foreground self-center">or</span>
                      </div>
                      <Input
                        type="text"
                        value={articleFormData.featured_image}
                        onChange={(e) => setArticleFormData({...articleFormData, featured_image: e.target.value})}
                        placeholder="Paste image URL here..."
                        className="bg-background border-border"
                        data-testid="article-image-url"
                      />
                      {articleFormData.featured_image && (
                        <div className="mt-3">
                          <img 
                            src={articleFormData.featured_image} 
                            alt="Preview" 
                            className="max-h-32 rounded border border-border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="article-excerpt" className="font-mono text-xs uppercase tracking-widest">Excerpt (Short Description)</Label>
                      <Textarea
                        id="article-excerpt"
                        value={articleFormData.excerpt}
                        onChange={(e) => setArticleFormData({...articleFormData, excerpt: e.target.value})}
                        className="bg-surface border-border"
                        rows={2}
                        data-testid="article-excerpt-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="article-content" className="font-mono text-xs uppercase tracking-widest">Content * (Markdown supported)</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const template = `Looking for the best NBA player props for [DATE]? Prime Stat Lab analyzes statistical distributions, hit rates, opportunity metrics, and matchup context to identify potential betting edges. Tonight's slate features three notable props involving [Player 1], [Player 2], and [Player 3].

## Best Prop Edge Tonight

According to the Prime Stat Lab model, the strongest statistical edge on tonight's slate is: **[Player Name] Over/Under [Line] [Prop Type]**. The current season median sits [X points] above the market line, creating one of the strongest structural cushions on the board.

---

## [Player 1] Over/Under [Line] [Prop Type]

**Market Line:** [Line]
**Season Median:** [Median]
**Gap:** [+X]

[Brief analysis of projections, hit rates, and matchup context]

**Key Signals:**
- Season median clears line by [X]
- Last 10/20 hit rate: [X%]
- [Matchup advantage/disadvantage]

**Grade:** [A/B/C] | **PSL Score:** [X/100]

---

## [Player 2] Over/Under [Line] [Prop Type]

**Market Line:** [Line]
**Season Median:** [Median]
**Gap:** [+X]

[Brief analysis]

**Key Signals:**
- [Signal 1]
- [Signal 2]

**Grade:** [A/B/C] | **PSL Score:** [X/100]

---

## [Player 3] Over/Under [Line] [Prop Type]

**Market Line:** [Line]
**Season Median:** [Median]
**Gap:** [+X]

[Brief analysis]

**Key Signals:**
- [Signal 1]
- [Signal 2]

**Grade:** [A/B/C] | **PSL Score:** [X/100]

---

## Model Edge Ranking

1. **[Player 1]** - [Grade] - Strongest edge
2. **[Player 2]** - [Grade]
3. **[Player 3]** - [Grade]`;
                            setArticleFormData({...articleFormData, content: template});
                            toast.success('SEO template loaded! Edit the placeholders.');
                          }}
                          className="text-xs"
                        >
                          Load SEO Template
                        </Button>
                      </div>
                      
                      {/* SEO Structure Guide */}
                      <div className="mb-2 p-3 bg-surface/50 border border-border rounded text-xs text-muted-foreground">
                        <strong className="text-foreground">SEO Structure Guide:</strong>
                        <ul className="mt-1 space-y-0.5 list-disc list-inside">
                          <li>Start with intro paragraph containing keywords</li>
                          <li>Add "Best Prop Edge Tonight" summary section</li>
                          <li>Use ## for each player prop (H2 headers)</li>
                          <li>Include key signals, grade, and PSL score</li>
                          <li>End with Model Edge Ranking</li>
                        </ul>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-2">
                        💡 <strong>Tip:</strong> When pasting from ChatGPT or other AI tools, use <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+Shift+V</kbd> (or <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Cmd+Shift+V</kbd> on Mac) to paste as plain text and preserve markdown formatting.
                      </p>
                      
                      {/* AI Content Assistant */}
                      {articleFormData.content && (
                        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-3 rounded-lg border border-purple-500/30 mb-3">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <Sparkles size={16} className="text-purple-400" />
                              <span className="text-sm font-medium text-purple-200">AI Content Assistant</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleAICleanup('format')}
                                disabled={cleaningContent}
                                className="gap-1 text-xs border-purple-500/50 hover:bg-purple-500/20"
                                data-testid="ai-format-button"
                              >
                                {cleaningContent ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                Fix Formatting
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleAICleanup('seo')}
                                disabled={cleaningContent}
                                className="gap-1 text-xs border-purple-500/50 hover:bg-purple-500/20"
                                data-testid="ai-seo-button"
                              >
                                {cleaningContent ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                Optimize SEO
                              </Button>
                            </div>
                          </div>
                          {cleanupChanges.length > 0 && (
                            <div className="mt-2 p-2 bg-surface/50 rounded text-xs">
                              <p className="text-muted-foreground font-medium mb-1">Changes made:</p>
                              <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                                {cleanupChanges.slice(0, 5).map((change, i) => (
                                  <li key={i}>{change}</li>
                                ))}
                                {cleanupChanges.length > 5 && (
                                  <li className="text-purple-400">...and {cleanupChanges.length - 5} more</li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <Textarea
                        id="article-content"
                        value={articleFormData.content}
                        onChange={(e) => setArticleFormData({...articleFormData, content: e.target.value})}
                        onPaste={(e) => {
                          e.preventDefault();
                          const htmlContent = e.clipboardData.getData('text/html');
                          const plainText = e.clipboardData.getData('text/plain');
                          
                          let pasteContent = plainText;
                          
                          // If HTML is available, convert to markdown to preserve formatting
                          if (htmlContent && htmlContent.trim()) {
                            try {
                              const td = new TurndownService({
                                headingStyle: 'atx',
                                bulletListMarker: '-',
                                codeBlockStyle: 'fenced',
                                hr: '---'
                              });
                              // Clean markdown from turndown
                              const markdown = td.turndown(htmlContent);
                              // Only use HTML-converted markdown if it has more structure than plain text
                              if (markdown && (markdown.includes('##') || markdown.includes('**') || markdown.includes('- '))) {
                                pasteContent = markdown;
                              }
                            } catch (err) {
                              // Fallback to plain text on error
                              console.warn('HTML-to-markdown conversion failed, using plain text');
                            }
                          }
                          
                          if (pasteContent) {
                            const textarea = e.target;
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const newValue = articleFormData.content.substring(0, start) + pasteContent + articleFormData.content.substring(end);
                            setArticleFormData({...articleFormData, content: newValue});
                            setTimeout(() => {
                              textarea.selectionStart = textarea.selectionEnd = start + pasteContent.length;
                            }, 0);
                          }
                        }}
                        required
                        className="bg-surface border-border min-h-[300px] font-mono text-sm"
                        data-testid="article-content-input"
                        placeholder="Paste your markdown content here...&#10;&#10;Use ## for headings&#10;Use **text** for bold&#10;Use *text* for italic&#10;Use - for bullet points"
                      />
                      
                      {/* Live Preview */}
                      {articleFormData.content && (
                        <div className="mt-4">
                          <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                            Live Preview
                          </Label>
                          <div className="bg-card border border-border rounded-lg p-4 prose prose-sm prose-invert max-w-none max-h-[300px] overflow-y-auto">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({children}) => <h1 className="text-2xl font-heading font-bold mt-4 mb-3 text-foreground">{children}</h1>,
                                h2: ({children}) => <h2 className="text-xl font-heading font-bold mt-4 mb-2 text-foreground border-b border-border pb-1">{children}</h2>,
                                h3: ({children}) => <h3 className="text-lg font-heading font-bold mt-3 mb-2 text-foreground">{children}</h3>,
                                p: ({children}) => <p className="mb-2 text-sm text-muted-foreground">{children}</p>,
                                ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1 text-sm text-muted-foreground">{children}</ul>,
                                ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1 text-sm text-muted-foreground">{children}</ol>,
                                li: ({children}) => <li className="text-sm">{children}</li>,
                                strong: ({children}) => <strong className="font-bold text-foreground">{children}</strong>,
                                em: ({children}) => <em className="italic">{children}</em>,
                                blockquote: ({children}) => <blockquote className="border-l-2 border-primary pl-3 my-2 italic text-muted-foreground text-sm">{children}</blockquote>,
                                a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>,
                                hr: () => <hr className="my-3 border-border" />,
                              }}
                            >
                              {articleFormData.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Find Related Articles Section */}
                    <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-bold text-primary flex items-center gap-2">
                            <ExternalLink size={16} />
                            AI-Powered Article Finder
                          </h4>
                          <p className="text-xs text-muted-foreground">Find related articles from ESPN, Bleacher Report, etc.</p>
                        </div>
                        <Button 
                          type="button"
                          onClick={findRelatedArticles} 
                          disabled={findingRelated || !articleFormData.title || !articleFormData.content}
                          className="gap-2"
                          data-testid="find-related-button"
                        >
                          {findingRelated ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Finding...
                            </>
                          ) : (
                            <>
                              <ExternalLink size={16} />
                              Find Related Articles
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {/* AI Suggestions */}
                      {suggestedSources.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-sm font-medium text-accent">Suggested External Links:</p>
                          {suggestedSources.map((source, index) => (
                            <div 
                              key={index} 
                              className="p-3 bg-surface border border-border rounded"
                            >
                              <a 
                                href={source.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-primary hover:underline"
                              >
                                {source.title}
                              </a>
                              <p className="text-xs text-muted-foreground mt-1">{source.relevance}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="article-published"
                        checked={articleFormData.published}
                        onChange={(e) => setArticleFormData({...articleFormData, published: e.target.checked})}
                        className="rounded"
                        data-testid="article-published-checkbox"
                      />
                      <Label htmlFor="article-published" className="font-mono text-xs uppercase tracking-widest">Published</Label>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setArticleDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-primary text-white" data-testid="save-article-button">
                        {editingArticle ? 'Update Article' : 'Create Article'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            </div>

            {articles.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <FileText size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-2">No articles yet</p>
                  <p className="text-sm text-muted-foreground">Create your first article to share insights with your audience</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {articles.map((article) => (
                  <Card key={article.id} className="bg-card border-border" data-testid={`article-${article.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-heading font-bold">{article.title}</h3>
                            {!article.published && (
                              <span className="bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded text-xs font-mono">DRAFT</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{article.excerpt}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            Created: {new Date(article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditArticle(article)}
                            data-testid={`edit-article-${article.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteArticle(article.id)}
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Challenge Entries Tab */}
        {activeTab === 'challenge' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold">Challenge Entries</h2>
                <p className="text-muted-foreground text-sm">Users who locked in picks to challenge you</p>
              </div>
              <Button
                onClick={fetchChallengeEntries}
                disabled={loadingEntries}
                variant="outline"
                data-testid="refresh-entries"
              >
                <RefreshCw className={`mr-2 ${loadingEntries ? 'animate-spin' : ''}`} size={16} />
                Refresh
              </Button>
            </div>

            {loadingEntries ? (
              <div className="text-center py-12">
                <Loader2 className="animate-spin mx-auto text-primary" size={32} />
              </div>
            ) : challengeEntries.length === 0 ? (
              <Card className="bg-surface border-border">
                <CardContent className="py-12 text-center">
                  <Swords className="mx-auto text-muted-foreground mb-4" size={48} />
                  <p className="text-muted-foreground">No challengers yet</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Export Button */}
                <div className="mb-4">
                  <Button
                    onClick={() => {
                      const csv = [
                        ['Name', 'Email', 'Date', 'Apps Downloaded', 'Picks'].join(','),
                        ...challengeEntries.map(entry => [
                          `"${entry.name || 'Anonymous'}"`,
                          `"${entry.email}"`,
                          `"${new Date(entry.created_at).toLocaleString()}"`,
                          `"${(entry.downloaded_apps || []).join(', ')}"`,
                          `"${(entry.picks || []).map(p => `${p.player_name || p.pick_id}: ${p.user_pick}`).join('; ')}"`
                        ].join(','))
                      ].join('\n');
                      
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `challengers_${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="bg-accent text-white hover:bg-accent/90"
                    data-testid="export-challengers"
                  >
                    <FileText className="mr-2" size={16} />
                    Export to CSV ({challengeEntries.length} entries)
                  </Button>
                </div>

                <div className="space-y-4">
                {challengeEntries.map((entry, index) => (
                  <Card key={entry.id || index} className="bg-surface border-border">
                    <CardContent className="py-4">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-lg">{entry.name || 'Anonymous'}</span>
                            <span className="text-sm text-muted-foreground">{entry.email}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            {new Date(entry.created_at).toLocaleString()}
                          </p>
                          {entry.downloaded_apps?.length > 0 && (
                            <div className="flex gap-2 mb-2">
                              {entry.downloaded_apps.map(app => (
                                <span key={app} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                                  {app}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="bg-background rounded p-3 min-w-[200px]">
                          <p className="text-xs text-muted-foreground mb-2">Their Picks:</p>
                          {entry.picks?.map((pick, i) => (
                            <div key={i} className="flex justify-between items-center py-1 border-b border-border last:border-0">
                              <span className="text-sm">{pick.player_name || pick.pick_id}</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                pick.user_pick === 'over' ? 'bg-green-600' : 'bg-red-600'
                              }`}>
                                {pick.user_pick?.toUpperCase()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Subscribers tab content - hidden for now, can be re-enabled later
        {activeTab === 'subscribers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold">Email Subscribers</h2>
                <p className="text-muted-foreground text-sm">Manage your notification email list</p>
              </div>
              <Dialog open={subscriberDialogOpen} onOpenChange={setSubscriberDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider" data-testid="add-subscriber-button">
                    <Plus className="mr-2" size={16} />
                    Add Subscriber
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">ADD SUBSCRIBER</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSubscriber} className="space-y-4">
                    <div>
                      <Label htmlFor="sub-email" className="font-mono text-xs uppercase tracking-widest">Email Address *</Label>
                      <Input
                        id="sub-email"
                        type="email"
                        value={newSubscriber.email}
                        onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                        required
                        className="bg-surface border-border"
                        data-testid="subscriber-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sub-name" className="font-mono text-xs uppercase tracking-widest">Name (Optional)</Label>
                      <Input
                        id="sub-name"
                        type="text"
                        value={newSubscriber.name}
                        onChange={(e) => setNewSubscriber({...newSubscriber, name: e.target.value})}
                        className="bg-surface border-border"
                        data-testid="subscriber-name-input"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setSubscriberDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-primary text-white" data-testid="save-subscriber-button">
                        Add Subscriber
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {subscribers.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Mail size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-2">No subscribers yet</p>
                  <p className="text-sm text-muted-foreground">Add email addresses to send pick notifications</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {subscribers.map((sub) => (
                  <Card key={sub.id} className="bg-card border-border" data-testid={`subscriber-${sub.id}`}>
                    <CardContent className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <Mail size={18} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-mono font-bold">{sub.email}</p>
                          {sub.name && <p className="text-sm text-muted-foreground">{sub.name}</p>}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSubscriber(sub.id)}
                        data-testid={`delete-subscriber-${sub.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="bg-surface-highlight border border-border p-6 rounded">
              <h3 className="font-heading font-bold mb-2 flex items-center gap-2">
                <Bell size={18} className="text-primary" />
                How Notifications Work
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Add email addresses to your subscriber list above</li>
                <li>• When you create a new pick, click the "Notify" button on that pick</li>
                <li>• All active subscribers will receive a beautifully formatted email with the pick details</li>
                <li>• Make sure to configure your SendGrid API key in the backend .env file</li>
              </ul>
            </div>
          </div>
        )}
        */}

        {activeTab === 'support' && (
          <div className="space-y-8" data-testid="support-tab-content">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleClearCache} className="bg-primary hover:bg-primary/90 font-mono text-xs uppercase gap-2" data-testid="clear-cache-btn">
                <RefreshCw size={14} /> Clear Cache & Refresh Data
              </Button>
              <Button variant="outline" onClick={fetchSupportData} className="font-mono text-xs uppercase gap-2" data-testid="refresh-tickets-btn">
                <RefreshCw size={14} /> Refresh Tickets
              </Button>
            </div>

            {/* Referral Stats */}
            {referralStats && (
              <div>
                <h3 className="font-heading font-bold text-lg mb-3">Referral Overview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="font-mono text-3xl font-bold text-accent">{referralStats.total_referrals}</div>
                    <div className="text-xs text-muted-foreground uppercase">Total Referrals</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="font-mono text-3xl font-bold text-primary">{referralStats.top_referrers?.length || 0}</div>
                    <div className="text-xs text-muted-foreground uppercase">Active Referrers</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="font-mono text-3xl font-bold">{referralStats.top_referrers?.[0]?.referral_count || 0}</div>
                    <div className="text-xs text-muted-foreground uppercase">Top Referrer Count</div>
                  </div>
                </div>
                {referralStats.top_referrers?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-card/80 font-mono text-xs uppercase tracking-wider text-muted-foreground grid grid-cols-4 border-b border-border">
                      <span>Username</span><span>Email</span><span>Code</span><span className="text-right">Referrals</span>
                    </div>
                    {referralStats.top_referrers.map((r, i) => (
                      <div key={r.id} className="px-4 py-2.5 grid grid-cols-4 items-center text-sm border-t border-border/50" data-testid={`referrer-${i}`}>
                        <span className="font-mono">{r.username}</span>
                        <span className="text-muted-foreground text-xs">{r.email}</span>
                        <span className="font-mono text-xs text-primary">{r.referral_code}</span>
                        <span className="text-right font-mono font-bold text-accent">{r.referral_count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Support Tickets */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-3">
                Support Tickets {supportNewCount > 0 && <span className="text-red-500 text-sm">({supportNewCount} new)</span>}
              </h3>
              {supportTickets.length === 0 ? (
                <p className="text-muted-foreground text-sm font-mono">No tickets yet - that's a good thing!</p>
              ) : (
                <div className="space-y-3">
                  {supportTickets.map(ticket => (
                    <div key={ticket.id} className={`bg-card border rounded-lg p-4 ${
                      ticket.status === 'new' ? 'border-red-500/50' : ticket.status === 'in_progress' ? 'border-yellow-500/50' : 'border-green-500/50'
                    }`} data-testid={`ticket-${ticket.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                              ticket.status === 'new' ? 'bg-red-500/20 text-red-400' :
                              ticket.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>{ticket.status.replace('_', ' ')}</span>
                            <span className="font-mono text-xs text-muted-foreground">#{ticket.id}</span>
                          </div>
                          <p className="font-bold text-sm">{ticket.subject}</p>
                          <p className="text-xs text-muted-foreground mt-1">{ticket.description}</p>
                          {ticket.page_url && <p className="text-[10px] text-primary mt-1 truncate">{ticket.page_url}</p>}
                          <p className="text-[10px] text-muted-foreground mt-1">{new Date(ticket.created_at).toLocaleString()}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleToggleTicketStatus(ticket.id)}
                          className="font-mono text-[10px] uppercase" data-testid={`ticket-toggle-${ticket.id}`}>
                          {ticket.status === 'new' ? 'Start' : ticket.status === 'in_progress' ? 'Resolve' : 'Reopen'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hidden thumbnail render container for bulk regeneration */}
      <div 
        ref={thumbRenderRef} 
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '1280px', height: '720px', overflow: 'hidden' }}
        aria-hidden="true"
      />

      {/* Bulk regen progress overlay */}
      {regenProgress && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card border border-border p-8 rounded-lg max-w-md w-full text-center">
            <Loader2 className="animate-spin mx-auto mb-4 text-primary" size={40} />
            <h3 className="font-heading text-xl font-bold mb-2">Regenerating Thumbnails</h3>
            <p className="text-muted-foreground text-sm mb-4">{regenProgress.status}</p>
            <div className="w-full bg-surface h-3 rounded overflow-hidden mb-2">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${(regenProgress.current / regenProgress.total) * 100}%` }}
              />
            </div>
            <p className="font-mono text-sm">{regenProgress.current} / {regenProgress.total}</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
